import type { HistoryDeliveryStatus } from '@skinstep/shared';
import { clickableProps } from '../lib/a11y';
import { formatAgo, formatShortDate } from '../lib/format';
import type { HistoryLot } from '../lib/history';
import { revealProps } from '../lib/motion';
import { SkinFrame } from './SkinFrame';
import { SkinThumbnail } from './SkinThumbnail';

// Outcome drives the whole card's accent: spine colour + the right-rail chip.
// Terminal states stay vivid, never washed out — a finished lot is still a result.
const OUTCOME_STYLE = {
  won: { spine: 'bg-success', glow: 'shadow-[0_0_8px_rgba(0,255,148,0.5)]', chip: 'text-success' },
  lost: { spine: 'bg-fg-muted/50', glow: '', chip: 'text-fg-muted' },
  cancelled: { spine: 'bg-warning/70', glow: '', chip: 'text-warning' },
} as const;

const OUTCOME_LABEL = { won: 'WON', lost: 'LOST', cancelled: 'CANCELLED' } as const;

function DeliveryBadge({
  status,
  holdUntil,
}: {
  status: HistoryDeliveryStatus;
  holdUntil: number | null;
}) {
  if (status === 'trade_sent') {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-wide text-success">
        <span aria-hidden>✓</span> Trade offer sent
      </span>
    );
  }
  if (status === 'awaiting_trade_offer') {
    return (
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-wide text-primary">
        <span className="w-1.5 h-1.5 rounded-full bg-primary anim-glow" /> Awaiting trade offer
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-wide text-warning">
      <span aria-hidden>⏱</span> Delivery holds until{' '}
      {holdUntil ? formatShortDate(holdUntil) : 'review'}
    </span>
  );
}

export function HistoryLotCard({
  lot,
  index,
  onSelect,
}: {
  lot: HistoryLot;
  index: number;
  onSelect?: () => void;
}) {
  const style = OUTCOME_STYLE[lot.outcome];
  const accent = lot.palette.accent ?? lot.palette.to;
  const winner = lot.winner;
  const winnerLabel = winner?.username ? `@${winner.username}` : (winner?.firstName ?? 'winner');
  const finalPrice = lot.finalPrice ?? lot.currentPrice;

  return (
    <article
      {...clickableProps(onSelect)}
      {...revealProps(index, {
        base: 240,
        step: 45,
        className: `relative grid grid-cols-[60px_1fr_auto] items-center gap-3 p-2.5 border border-edge/70 bg-bg-elev/30 ${onSelect ? 'cursor-pointer hover:border-edge-bright transition-colors' : ''}`,
      })}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-[2px] ${style.spine} ${style.glow}`} />

      <div className="relative overflow-hidden" style={{ aspectRatio: '1 / 0.8' }}>
        {lot.imageUrl ? (
          <SkinThumbnail
            imageUrl={lot.imageUrl}
            alt={`${lot.weaponLabel} | ${lot.skinName}`}
            weapon={lot.weapon}
            palette={lot.palette}
            rarityColor={lot.rarityColor ?? null}
            size="sm"
          />
        ) : (
          <SkinFrame weapon={lot.weapon} palette={lot.palette} size="sm" showPattern={false} />
        )}
      </div>

      <div className="min-w-0 flex flex-col gap-1">
        <p className="font-display text-[12px] leading-tight truncate">
          {lot.isStatTrak && <span className="text-[#cf6a32]">ST™ </span>}
          <span className="text-fg">{lot.weaponLabel}</span>{' '}
          <span style={{ color: accent }}>| {lot.skinName}</span>
        </p>
        <p className="font-mono text-[10px] text-fg-dim">
          {lot.userBidCount} {lot.userBidCount === 1 ? 'bid' : 'bids'} ·{' '}
          {formatAgo(lot.closedAt ?? Date.now())}
        </p>

        <div className="flex items-center gap-2 min-h-[16px]">
          {lot.outcome === 'won' && lot.deliveryStatus && (
            <DeliveryBadge status={lot.deliveryStatus} holdUntil={lot.deliveryHoldUntil} />
          )}

          {lot.outcome === 'lost' && (
            <span className="font-mono text-[10px] text-fg-dim truncate">
              claimed by <span className="text-fg-muted">{winnerLabel}</span>
            </span>
          )}

          {lot.outcome === 'cancelled' && (
            <span className="font-mono text-[10px] tracking-wide text-warning">
              Under 5 players · bids refunded
            </span>
          )}
        </div>
      </div>

      <div className="text-right flex flex-col items-end gap-1.5">
        <div className={`font-mono text-[9px] tracking-[0.2em] font-semibold ${style.chip}`}>
          {OUTCOME_LABEL[lot.outcome]}
        </div>
        {lot.outcome === 'cancelled' ? (
          <div className="font-mono text-[10px] text-fg-dim">voided</div>
        ) : (
          <div
            className={`font-mono font-bold text-[14px] tabular-nums leading-none ${lot.outcome === 'won' ? 'text-success' : 'text-fg-muted'}`}
          >
            ${finalPrice.toFixed(2)}
          </div>
        )}

        {lot.outcome === 'lost' && lot.buyNowAvailable && lot.buyNowPriceCents != null && (
          <span className="font-mono text-[10px] font-semibold text-accent border border-accent/50 bg-accent/10 px-2 py-1 whitespace-nowrap">
            BUY NOW ${(lot.buyNowPriceCents / 100).toFixed(2)}
          </span>
        )}

        {lot.outcome === 'won' && lot.deliveryStatus === 'delayed_by_win_cap' && (
          <span className="font-mono text-[10px] font-semibold text-success border border-success/50 bg-success/10 px-2 py-1 whitespace-nowrap">
            CLAIM
          </span>
        )}

        {lot.outcome === 'won' && lot.deliveryStatus === 'awaiting_trade_offer' && (
          <span className="font-mono text-[10px] font-semibold text-primary border border-primary/50 bg-primary/10 px-2 py-1 whitespace-nowrap">
            TRADE
          </span>
        )}
      </div>
    </article>
  );
}
